// Path: ./src/utils/apiError.ts

export interface ApiErrorInfo {
  /** HTTP status, when the server answered at all. Missing for network errors. */
  status?: number;
  message: string;
  /** Every problem the server listed for a rejected body, when it listed them. */
  validationErrors?: string[];
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const toMessage = (value: unknown): string | undefined => {
  if (typeof value === "string" && value) return value;
  if (Array.isArray(value) && value.length) return value.join(", ");
  return undefined;
};

const toValidationErrors = (value: unknown): string[] | undefined => {
  if (!Array.isArray(value)) return undefined;
  const errors = value.filter(
    (item): item is string => typeof item === "string" && item !== "",
  );
  return errors.length ? errors : undefined;
};

/**
 * Turns whatever the generated SDK threw into a status + readable message.
 *
 * The SDK throws the raw `Response` for non-2xx answers and a `TypeError` for
 * network failures. Only parse the body as JSON when the server says it is
 * JSON — an HTML 502 page from the proxy would otherwise throw here.
 */
export const readApiError = async (error: unknown): Promise<ApiErrorInfo> => {
  if (error instanceof Response) {
    const fallback =
      `${error.status} ${error.statusText}`.trim() || "Request failed";
    const contentType = error.headers.get("content-type") ?? "";
    if (!contentType.includes("json")) {
      return { status: error.status, message: fallback };
    }
    try {
      const body: unknown = await error.clone().json();
      if (!isRecord(body)) {
        return { status: error.status, message: fallback };
      }
      const message = toMessage(body.message) ?? toMessage(body.error);
      const validationErrors = toValidationErrors(body.validationErrors);
      return {
        status: error.status,
        message: message ?? fallback,
        ...(validationErrors ? { validationErrors } : {}),
      };
    } catch {
      return { status: error.status, message: fallback };
    }
  }
  if (error instanceof Error) {
    return { message: error.message || error.name };
  }
  return { message: String(error) };
};

const getAuthorizationHeader = (arg: unknown): string | undefined => {
  if (!isRecord(arg) || !isRecord(arg.headers)) return undefined;
  const value = arg.headers.Authorization;
  return typeof value === "string" ? value : undefined;
};

/** True for an `Authorization` value with a non-empty Bearer token. */
export const isBearerToken = (value: string | null | undefined): boolean =>
  /^Bearer\s+\S+/.test(value ?? "");

/** True when one of the SDK call's arguments carries a non-empty Bearer token. */
export const sentBearerToken = (args: unknown[]): boolean =>
  args.some((arg) => isBearerToken(getAuthorizationHeader(arg)));

/**
 * Copies the SDK call's arguments with the Bearer token swapped for `token`,
 * so a request that failed with an expired token can be replayed after the
 * user signs in again.
 */
export const withBearerToken = (args: unknown[], token: string): unknown[] =>
  args.map((arg) => {
    if (getAuthorizationHeader(arg) === undefined) return arg;
    const options = arg as { headers: Record<string, unknown> };
    return {
      ...options,
      headers: { ...options.headers, Authorization: `Bearer ${token}` },
    };
  });
